import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Spinner } from 'reactstrap';
import WelcomeScreen from './WelcomeScreen';
import { StepShellContext } from './StepShellContext';
import StepRenderer from './StepRenderer';
import useDocumentsFlow from './hooks/useDocumentsFlow';
import useDraftPersistence from './hooks/useDraftPersistence';
import buildSteps from './utils/buildSteps';
import filterRequestedSteps from './utils/filterRequestedSteps';
import { colors, layout } from './styles';

type ScreenMode = 'welcome' | 'step' | 'done';

const NewHireDocumentsV2 = () => {
  const { token } = useParams<{ token: string }>();
  const flow = useDocumentsFlow(token);
  const drafts = useDraftPersistence(token);
  const { search } = useLocation();
  const onlyRequestedDocuments = new URLSearchParams(search).get('scope') === 'requested';

  const [screen, setScreen] = useState<ScreenMode>('welcome');
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);

  const steps = useMemo(() => {
    if (!flow.tokenData) return [];
    const allSteps = buildSteps({
      tokenData: flow.tokenData,
      signedDocs: flow.signedDocs,
      draftDocs: flow.draftDocs,
      application: flow.application,
      applicationDraft: flow.applicationDraft,
      bankingAccounts: flow.bankingAccounts,
      localDrafts: drafts.drafts,
    });
    return onlyRequestedDocuments ? filterRequestedSteps(allSteps) : allSteps;
  }, [
    onlyRequestedDocuments,
    flow.tokenData,
    flow.signedDocs,
    flow.draftDocs,
    flow.application,
    flow.applicationDraft,
    flow.bankingAccounts,
    drafts.drafts,
  ]);

  const currentStepIndex = steps.findIndex((s) => s.id === currentStepId);
  const currentStep = currentStepIndex >= 0 ? steps[currentStepIndex] : null;

  const visibleSteps = steps.filter((s) => s.kind !== 'REVIEW');
  const completedCount = visibleSteps.filter((s) => s.status === 'completed').length;

  useEffect(() => {
    if (screen === 'step' && !currentStep && steps.length > 0) {
      const firstPending = steps.find((s) => s.status !== 'completed') || steps[0];
      setCurrentStepId(firstPending.id);
    }
  }, [screen, currentStep, steps]);

  const goWelcome = () => {
    setScreen('welcome');
    setCurrentStepId(null);
  };

  const handleStart = () => {
    if (!steps.length) return;
    const firstPending = steps.find((s) => s.status !== 'completed') || steps[0];
    setCurrentStepId(firstPending.id);
    setScreen('step');
  };

  const handleJumpTo = (stepId: string) => {
    setCurrentStepId(stepId);
    setScreen('step');
  };

  const handleNext = async () => {
    if (currentStepIndex < 0) return;
    const remaining = steps.slice(currentStepIndex + 1);
    const nextPending = remaining.find((s) => s.status !== 'completed') || remaining[0];
    if (nextPending) {
      setCurrentStepId(nextPending.id);
    } else {
      goWelcome();
    }
    await flow.refresh();
  };

  const handlePrevious = () => {
    if (currentStepIndex <= 0) {
      goWelcome();
      return;
    }
    const prevStep = steps[currentStepIndex - 1];
    setCurrentStepId(prevStep.id);
  };

  const handleSaveLater = () => {
    toast.success('Progress saved. You can continue later from the link in your email.');
    goWelcome();
  };

  const handleAllDone = () => {
    drafts.clearAll();
    setScreen('done');
  };

  if (flow.isLoading) {
    return (
      <div style={layout.page}>
        <div
          style={{
            ...layout.container,
            maxWidth: 600,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 80,
          }}
        >
          <Spinner style={{ color: colors.accent, width: 40, height: 40 }} />
        </div>
      </div>
    );
  }

  if (!flow.isValidToken || !flow.tokenData) {
    return (
      <div style={layout.page}>
        <div style={{ ...layout.container, maxWidth: 600 }}>
          <div style={{ ...layout.card, textAlign: 'center', padding: '48px 32px' }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>This link is no longer valid</h1>
            <p style={{ color: colors.muted, marginTop: 10 }}>Please contact your hiring manager for a new link.</p>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'done') {
    return (
      <div style={layout.page}>
        <div style={{ ...layout.container, maxWidth: 600 }}>
          <div style={{ ...layout.card, textAlign: 'center', padding: '48px 32px' }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: '#D1FAE5',
                color: colors.success,
                fontSize: 36,
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="fas fa-check" />
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Paperwork Submitted</h1>
            <p style={{ color: colors.muted, marginTop: 10 }}>
              Thank you! Your onboarding paperwork has been submitted successfully. Your employer will be in touch with
              next steps.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'welcome' || !currentStep) {
    return <WelcomeScreen steps={steps} userName={flow.tokenData?.userName} onStart={handleStart} />;
  }

  const stepDraft = drafts.getDraft(currentStep.id);

  return (
    <StepShellContext.Provider value={{ userName: flow.tokenData?.userName }}>
      <StepRenderer
        step={currentStep}
        steps={steps}
        stepIndex={currentStepIndex}
        totalSteps={visibleSteps.length}
        completedSteps={completedCount}
        token={token}
        tokenData={flow.tokenData}
        application={flow.application}
        applicationDraft={flow.applicationDraft}
        signedDocs={flow.signedDocs}
        draftDocs={flow.draftDocs}
        bankingAccounts={flow.bankingAccounts}
        uploadedDocuments={flow.uploadedDocuments}
        draft={stepDraft}
        saveDraft={(value) => drafts.saveDraft(currentStep.id, value)}
        clearDraft={() => drafts.clearDraft(currentStep.id)}
        onBack={goWelcome}
        onPrevious={currentStepIndex > 0 ? handlePrevious : undefined}
        onNext={handleNext}
        onSaveLater={handleSaveLater}
        onJumpTo={handleJumpTo}
        onComplete={handleAllDone}
      />
    </StepShellContext.Provider>
  );
};

export default NewHireDocumentsV2;
